import { IComboBox, IComboBoxOption, IComboBoxStyles } from '@fluentui/react';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import React, { useEffect, useState } from 'react';
import { parseTime } from 'utils/datetime';

interface ITimePicker {
  selectedTime: Date | undefined;
  minTime?: number | Date; //in minutes 12:05 = 12*60+ 5 -> 725, must be >=0 and < 1440 | time part of Date
  onUpdateTime: (hours: number, mins: number) => void;
  onClearTime?: () => void;
  label?: string;
  error?: string | undefined;
  disabled?: boolean;
  pickerWidth?: number;
  allowEmptyValue?: boolean;
}

const getMinuteFromDate = (date: Date | undefined): number | undefined => {
  if (!date) return undefined;

  return new Date(date).getHours() * 60 + new Date(date).getMinutes();
};

const minuteToTime = (min: number): string => {
  const hours = Math.floor(min / 60);
  const mins = min - hours * 60;

  return `${hours < 10 ? '0' + hours.toString() : hours.toString()}:${
    mins < 10 ? '0' + mins.toString() : mins.toString()
  }`;
};

const validateInput = (text: string | undefined) => {
  if (!text || text.length !== 5) return false;
  if (text[2] !== ':') return false;
  const [hours, mins] = text.split(':');
  if (Number.isNaN(+hours)) return false;
  if (Number.isNaN(+mins)) return false;
  if (+hours > 23 || +hours < 0) return false;
  if (+mins > 59 || +mins < 0) return false;

  return true;
};

const getMinTimeInMinutes = (min: number | Date | undefined): number => {
  if (!min) return 0;
  if (typeof min === 'number' && (min <= 0 || min >= 1440)) return 0;
  if (typeof min === 'number') return min;

  return min.getHours() * 60 + min.getMinutes();
};

const comboBoxDefWidth = 100;
const comboBoxStyles: Partial<IComboBoxStyles> = { root: { width: comboBoxDefWidth } };

const TimePicker = (props: ITimePicker) => {
  const getInitialTimeOptions = (): IComboBoxOption[] => {
    const options: IComboBoxOption[] = Array.from({ length: 48 }, (_, i) => {
      return {
        key: i * 30,
        text: minuteToTime(i * 30),
      };
    });

    if (props.allowEmptyValue) {
      options.unshift({
        key: -1,
        text: '',
      });
    }

    return options;
  };

  const [timeOptions, setTimeOptions] = useState<IComboBoxOption[]>(getInitialTimeOptions());
  const [comboxStyles, setComboxStyles] = useState<Partial<IComboBoxStyles>>(comboBoxStyles);
  const [selectedKey, setSelectedKey] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!props.selectedTime && props.allowEmptyValue) {
      setSelectedKey(-1);

      return;
    }

    const min = getMinuteFromDate(props.selectedTime);
    if (min === undefined) return;

    const existingItem = timeOptions.find((itm) => {
      return itm.key === min;
    });

    if (existingItem) {
      setSelectedKey(existingItem.key as number);
    } else {
      //add custom time to combo as hidden
      setTimeOptions([
        ...timeOptions,
        {
          key: min,
          text: minuteToTime(min),
          hidden: min % 30 !== 0,
        },
      ]);

      setSelectedKey(min);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedTime]);

  useEffect(() => {
    const calculatedWidth = props.pickerWidth ? props.pickerWidth : comboBoxDefWidth;
    const updatedBoxStyles: Partial<IComboBoxStyles> = { root: { width: calculatedWidth } };
    setComboxStyles(updatedBoxStyles);
  }, [props.pickerWidth]);

  //
  // Main render
  //
  return (
    <IPComboBox
      persistMenu
      allowFreeform
      useComboBoxAsMenuWidth
      selectedKey={selectedKey}
      errorMessage={props.error}
      disabled={props.disabled}
      styles={comboxStyles}
      calloutProps={{ calloutMaxHeight: 250 }}
      options={timeOptions}
      label={props.label}
      onChange={(event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string) => {
        let key: number | undefined = undefined;

        //validate user input
        if (!option) {
          if (!value) return;
          value = parseTime(value);
          if (!value || !validateInput(value)) return;

          const [hourString, minString] = value.split(':');
          key = +hourString * 60 + +minString;
        }

        // set key from option if not from user input
        if (key === undefined && option) {
          key = +option.key;

          //handle empty value
          if (key === -1 && props.allowEmptyValue) {
            if (props.onClearTime) props.onClearTime();
            setSelectedKey(key);

            return;
          }
        }

        //set selected key
        if (key === undefined) return;
        setSelectedKey(key);

        //validate min time and update
        if (props.minTime) {
          if (key > getMinTimeInMinutes(props.minTime)) {
            key = getMinTimeInMinutes(props.minTime);
          }
          const hours = Math.floor(key / 60);
          const mins = key - hours * 60;
          props.onUpdateTime(hours, mins);
        } else {
          const hours = Math.floor(key / 60);
          const mins = key - hours * 60;
          props.onUpdateTime(hours, mins);
        }
      }}
    />
  );
};

export default TimePicker;
